import { cn } from '@/lib/utils'
import { formatDateTime } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Bot, User, Headset } from 'lucide-react'
import type { SupportMessage } from '@/types/entities'

interface ConversationThreadProps {
  messages: SupportMessage[]
  isLoading?: boolean
}

export function ConversationThread({ messages, isLoading }: ConversationThreadProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    )
  }

  if (messages.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No messages in this ticket yet.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {messages.map((message) => (
        <MessageBubble key={message.id} message={message} />
      ))}
    </div>
  )
}

function MessageBubble({ message }: { message: SupportMessage }) {
  const isInbound = message.direction === 'inbound'
  const isDraft = message.metadata && (message.metadata as Record<string, unknown>).draft === true

  const senderIcon = {
    customer: <User className="h-4 w-4" />,
    agent: <Bot className="h-4 w-4" />,
    operator: <Headset className="h-4 w-4" />,
  }

  const senderLabel = {
    customer: 'Customer',
    agent: 'AI Agent',
    operator: 'Operator',
  }

  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        isInbound
          ? 'bg-muted/30 border-border'
          : isDraft
            ? 'bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800'
            : 'bg-primary/5 border-primary/20'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={cn(
            'flex items-center justify-center h-6 w-6 rounded-full text-white',
            isInbound ? 'bg-slate-500' : message.senderType === 'agent' ? 'bg-violet-500' : 'bg-blue-500'
          )}>
            {senderIcon[message.senderType]}
          </span>
          <span className="text-sm font-medium">
            {senderLabel[message.senderType]}
          </span>
          {isInbound && (
            <span className="text-xs text-muted-foreground">
              {message.fromAddress}
            </span>
          )}
          {isDraft && (
            <Badge variant="outline" className="text-xs bg-amber-100 text-amber-800 border-amber-300">
              Draft
            </Badge>
          )}
          {message.bouncedAt && (
            <Badge variant="destructive" className="text-xs">
              Bounced
            </Badge>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {formatDateTime(message.createdAt)}
        </span>
      </div>

      {/* Subject */}
      {message.subject && (
        <p className="text-sm font-medium mb-1">{message.subject}</p>
      )}

      {/* Body */}
      <div className="text-sm whitespace-pre-wrap break-words">
        {message.bodyText || (
          message.bodyHtml ? (
            <div
              className="prose prose-sm max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: message.bodyHtml }}
            />
          ) : (
            <span className="text-muted-foreground italic">No content</span>
          )
        )}
      </div>

      {/* Attachments indicator */}
      {message.hasAttachments && (
        <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
          📎 Has attachments
        </div>
      )}
    </div>
  )
}
